// packages/ui/src/SplitScreenLayout.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { GripHorizontal, GripVertical } from 'lucide-react';

export interface SplitScreenLayoutProps {
  children: [React.ReactNode, React.ReactNode];
  orientation?: 'horizontal' | 'vertical';
  initialSizes?: [number, number];
  minPaneSize?: number;
  onResize?: (sizes: [number, number]) => void;
  splitterClassName?: string;
  paneClassName?: string;
  firstPaneClassName?: string;
  secondPaneClassName?: string;
  className?: string; // FIX: Added className prop to allow custom styling (replaces direct 'style')
  gutterSize?: number;
}

export const SplitScreenLayout: React.FC<SplitScreenLayoutProps> = ({
  children,
  orientation = 'horizontal',
  initialSizes = [50, 50],
  minPaneSize = 50,
  onResize,
  splitterClassName,
  paneClassName,
  firstPaneClassName,
  secondPaneClassName,
  className, // FIX: New prop
  gutterSize = 8,
}) => {
  const childCount = React.Children.count(children);

  const [pane1Size, setPane1Size] = useState(initialSizes[0]);

  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const isHorizontal = orientation === 'horizontal';

  const handleMouseDown = useCallback(() => {
    isDraggingRef.current = true;
    document.body.style.userSelect = 'none';
    if (isHorizontal) {
        document.body.style.cursor = 'col-resize';
    } else {
        document.body.style.cursor = 'row-resize';
    }
  }, [isHorizontal]);

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (onResize) {
        onResize([pane1Size, 100 - pane1Size]);
      }
    }
  }, [pane1Size, onResize]);

  const handleMouseMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDraggingRef.current || !containerRef.current) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const containerRect = containerRef.current.getBoundingClientRect();
    let newPane1Percent: number;

    if (isHorizontal) {
      const mouseX = clientX - containerRect.left;
      const totalWidth = containerRect.width;
      const rawPercent = (mouseX / totalWidth) * 100;

      const gutterPercent = (gutterSize / totalWidth) * 100;
      newPane1Percent = rawPercent - gutterPercent / 2;

      const minPercent = (minPaneSize / totalWidth) * 100;
      const maxPercent = 100 - minPercent - gutterPercent;
      newPane1Percent = Math.max(minPercent, Math.min(newPane1Percent, maxPercent));

    } else {
      const mouseY = clientY - containerRect.top;
      const totalHeight = containerRect.height;
      const rawPercent = (mouseY / totalHeight) * 100;

      const gutterPercent = (gutterSize / totalHeight) * 100;
      newPane1Percent = rawPercent - gutterPercent / 2;

      const minPercent = (minPaneSize / totalHeight) * 100;
      const maxPercent = 100 - minPercent - gutterPercent;
      newPane1Percent = Math.max(minPercent, Math.min(newPane1Percent, maxPercent));
    }

    setPane1Size(newPane1Percent);
  }, [isHorizontal, minPaneSize, gutterSize]);


  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [handleMouseMove, handleMouseUp]);

  const pane1Style: React.CSSProperties = isHorizontal
    ? { width: `calc(${pane1Size}% - ${gutterSize / 2}px)` }
    : { height: `calc(${pane1Size}% - ${gutterSize / 2}px)` };

  const pane2Style: React.CSSProperties = isHorizontal
    ? { width: `calc(${100 - pane1Size}% - ${gutterSize / 2}px)` }
    : { height: `calc(${100 - pane1Size}% - ${gutterSize / 2}px)` };

  if (childCount !== 2) {
    console.error("SplitScreenLayout expects exactly two children.");
    return <div className="text-red-500">Error: SplitScreenLayout requires two children.</div>;
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        "flex w-full h-full overflow-hidden bg-white border-2 border-black rounded-xl", // Neobrutalist container
        "[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20",
        "[.theme-liquid-glass_&]:rounded-3xl [.theme-liquid-glass_&]:backdrop-blur-xl",
        isHorizontal ? 'flex-row' : 'flex-col',
        className // FIX: Apply className here for custom styling
      )}
    >
      <div
        className={clsx("overflow-auto", paneClassName, firstPaneClassName)}
        style={pane1Style}
      >
        {children[0]}
      </div>

      <div
        role="separator"
        aria-orientation={orientation}
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        className={clsx(
          "flex-shrink-0 bg-slate-200 border-black flex items-center justify-center",
          "transition-colors duration-150",
          "hover:bg-slate-300 active:bg-slate-400",
          "border-2 border-black shadow-[inset_0_0_0_1px_white,1px_1px_0_0_black] hover:shadow-[inset_0_0_0_1px_white,2px_2px_0_0_black] active:shadow-[inset_0_0_0_1px_white,0px_0px_0_0_black]",
          "[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20",
          "[.theme-liquid-glass_&]:hover:bg-white/20 [.theme-liquid-glass_&]:active:bg-white/15",
          "[.theme-liquid-glass_&]:shadow-none",
          isHorizontal ? 'cursor-col-resize border-y-0' : 'cursor-row-resize border-x-0',
          splitterClassName
        )}
        style={isHorizontal ? { width: `${gutterSize}px` } : { height: `${gutterSize}px` }}
        aria-label={`Resize ${orientation} split`}
      >
        {isHorizontal ? (
          <GripVertical size={gutterSize * 2} className="text-slate-600 pointer-events-none [.theme-liquid-glass_&]:text-white/50" />
        ) : (
          <GripHorizontal size={gutterSize * 2} className="text-slate-600 pointer-events-none [.theme-liquid-glass_&]:text-white/50" />
        )}
      </div>

      <div
        className={clsx("overflow-auto", paneClassName, secondPaneClassName)}
        style={pane2Style}
      >
        {children[1]}
      </div>
    </div>
  );
};
